import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useRxCollection } from 'rxdb/plugins/react';
import type { FindingDocType } from '@ast-data/shared';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { useAbility } from '../auth/abilityContext';
import { historyRecordRequires } from '../history/historyRecordPath';
import type { DeliverableGap } from './deliverableTexts';
import {
    gapFieldLabel,
    gapRecordLabel,
    gapRecordPath,
    groupGapsByRecord,
    sortGapRecords
} from './translationGapRecords';

/**
 * The records behind a translation warning, each linked to its screen so the
 * translation can be looked at and accepted there.
 *
 * The links open a NEW tab: the export is waiting on this dialog, and the form
 * it may have been started from can hold unsaved changes — leaving the page
 * would cost both.
 */
export function TranslationGapList({
    gaps
}: {
    gaps: readonly DeliverableGap[];
}) {
    const intl = useIntl();
    const ability = useAbility();
    const findings = useRxCollection<FindingDocType>('findings');
    const records = useMemo(() => groupGapsByRecord(gaps), [gaps]);

    // A description is named through its Befund, which the gap does not carry.
    const findingIds = useMemo(
        () => [
            ...new Set(
                records
                    .map((entry) => String(entry.record.findingId ?? ''))
                    .filter((id) => id !== '')
            )
        ],
        [records]
    );
    const [findingNumbers, setFindingNumbers] = useState<
        ReadonlyMap<string, string>
    >(new Map());
    useEffect(() => {
        if (!findings || findingIds.length === 0) {
            return;
        }
        let active = true;
        void findings
            .findByIds(findingIds)
            .exec()
            .then((found) => {
                if (active) {
                    setFindingNumbers(
                        new Map(
                            [...found.values()].map((finding) => [
                                finding.id,
                                finding.findingNumber
                            ])
                        )
                    );
                }
            });
        return () => {
            active = false;
        };
    }, [findings, findingIds]);

    const rows = useMemo(
        () =>
            sortGapRecords(
                records.map((entry) => ({
                    entry,
                    label: gapRecordLabel(intl, entry, (findingId) =>
                        findingNumbers.get(findingId)
                    )
                }))
            ),
        [records, findingNumbers, intl]
    );

    return (
        <Box sx={{ maxHeight: 280, overflowY: 'auto', mt: 1 }}>
            <List dense disablePadding>
                {rows.map(({ entry, label }) => {
                    const requires = historyRecordRequires(entry.ownerType);
                    const path =
                        !requires || ability.can(...requires)
                            ? gapRecordPath(entry)
                            : null;
                    // The field is named only where it has to tell several
                    // apart; a lone one is found by opening the record.
                    const details = entry.fields
                        .map(({ field, reason }) => {
                            const why = intl.formatMessage({
                                id: `export.translationGaps.${reason}`
                            });
                            return entry.fields.length === 1
                                ? why
                                : `${gapFieldLabel(intl, entry.ownerType, field)}: ${why}`;
                        })
                        .join(' · ');
                    return (
                        <ListItem key={entry.key} disableGutters>
                            <ListItemText
                                primary={
                                    path ? (
                                        <Link
                                            component={RouterLink}
                                            to={path}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            {label}
                                            <OpenInNewIcon fontSize="inherit" />
                                        </Link>
                                    ) : (
                                        label
                                    )
                                }
                                secondary={details}
                            />
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
}
